import { useState } from "react"
import { Search, X, ArrowRight } from "lucide-react"
import RoleTaxonomyFilter from "./RoleTaxonomyFilter"
import RoleSelectionModal from "./RoleSelectionModal"

const FF = "'Plus Jakarta Sans', 'Inter', sans-serif"

// ════════════════════════════════════════════════════════════════
export default function FilterBar({
  C,
  selectedTaxonomies, onTaxonomyToggle, onTaxonomyClear,
  selectedCompanies = [], onCompanyToggle, onCompanyClear,
  onSearch,
}) {
  const totalSelected = selectedTaxonomies.length + selectedCompanies.length
  const [query, setQuery] = useState("")
  const [open, setOpen]   = useState(false)
  const [modalOpen, setModalOpen] = useState(false)
  return (
    <>
      <style>{`
        .fbar {
          display: flex;
          align-items: center;
          width: 100%;
          min-width: 600px;
          max-width: 720px;
        }

        /* ── Unified search bar (clean white pill) ── */
        .fbar-search {
          flex: 1;
          min-width: 0;
          display: flex;
          align-items: center;
          gap: 12px;
          height: 48px;
          padding: 4px 4px 4px 22px;
          background: #ffffff;
          border: 1px solid #ededed;
          border-radius: 9999px;
          cursor: text;
          transition: border-color 0.15s, box-shadow 0.15s;
          box-shadow: 0 1px 2px rgba(0,0,0,0.02);
        }
        .fbar-search:hover {
          border-color: #e0e0e0;
          box-shadow: 0 2px 6px rgba(0,0,0,0.04);
        }
        .fbar-search.open {
          border-color: #d4d4d8;
          box-shadow: 0 2px 10px rgba(0,0,0,0.06);
        }
        .fbar-search-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px; height: 24px;
          color: #a1a1aa; flex-shrink: 0;
        }
        .fbar-search-input {
          flex: 1;
          min-width: 0;
          height: 100%;
          background: transparent;
          border: none;
          outline: none;
          font-family: ${FF};
          font-size: 14px;
          font-weight: 400;
          color: #18181b;
          letter-spacing: -0.005em;
        }
        .fbar-search-input::placeholder { color: #9a9a9a; }
        .fbar-search-clear {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 24px; height: 24px;
          background: #f4f4f5;
          border: none;
          border-radius: 9999px;
          color: #71717a;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.12s, color 0.12s;
        }
        .fbar-search-clear:hover { background: #ececec; color: #18181b; }
        .fbar-search-count {
          display: inline-flex; align-items: center;
          height: 24px; padding: 0 10px;
          background: #f4f4f5;
          border: none;
          border-radius: 9999px;
          font-size: 11.5px;
          font-weight: 600;
          color: #52525b;
          font-family: ${FF};
          flex-shrink: 0;
          white-space: nowrap;
          cursor: pointer;
          transition: background 0.12s, color 0.12s;
        }
        .fbar-search-count:hover { background: #18181b; color: #ffffff; }

        /* Search CTA inside the bar */
        .fbar-cta {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          height: 40px;
          padding: 0 20px;
          background: #121212;
          color: #ffffff;
          border: none;
          border-radius: 9999px;
          font-size: 13px;
          font-weight: 600;
          font-family: ${FF};
          cursor: pointer;
          transition: background 0.15s;
          white-space: nowrap;
          flex-shrink: 0;
        }
        .fbar-cta:hover { background: #000000; }
        .fbar-cta-arrow { transition: transform 0.2s; }
        .fbar-cta:hover .fbar-cta-arrow { transform: translateX(2px); }

        @media (max-width: 760px) {
          .fbar-search { height: auto; flex-wrap: wrap; padding: 8px 8px 8px 16px; }
          .fbar-cta { width: 100%; justify-content: center; }
        }
      `}</style>

      <div className="fbar">

        {/* ── Unified Search bar ── */}
        <RoleTaxonomyFilter
          selected={selectedTaxonomies}
          onToggle={onTaxonomyToggle}
          onClear={onTaxonomyClear}
          selectedCompanies={selectedCompanies}
          onCompanyToggle={onCompanyToggle}
          onCompanyClear={onCompanyClear}
          C={C}
          wrapperStyle={{ flex: 1, minWidth: 0 }}
          query={query}
          openProp={open}
          onRequestClose={() => setOpen(false)}
          renderTrigger={() => (
            <div className={`fbar-search${open ? " open" : ""}`}>
              <span className="fbar-search-icon">
                <Search size={16} strokeWidth={2} />
              </span>

              <input
                type="text"
                className="fbar-search-input"
                placeholder="Search roles, or companies"
                value={query}
                onChange={e => { setQuery(e.target.value); setOpen(true) }}
                onFocus={() => setOpen(true)}
              />
              {query && (
                <button
                  type="button"
                  aria-label="Clear search"
                  onClick={e => { e.stopPropagation(); setQuery("") }}
                  className="fbar-search-clear"
                >
                  <X size={12} strokeWidth={2.2} />
                </button>
              )}
              {totalSelected > 0 && (
                <button
                  type="button"
                  className="fbar-search-count"
                  onClick={e => { e.stopPropagation(); setOpen(false); setModalOpen(true) }}
                  title="Edit selection"
                >
                  {totalSelected} selected
                </button>
              )}

              {/* Search CTA */}
              <button
                type="button"
                onClick={e => { e.stopPropagation(); onSearch?.(); setOpen(false) }}
                className="fbar-cta"
              >
                Search
                <ArrowRight size={14} strokeWidth={2.4} className="fbar-cta-arrow" />
              </button>
            </div>
          )}
        />
      </div>

      <RoleSelectionModal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        selected={selectedTaxonomies}
        onToggle={onTaxonomyToggle}
        onClear={onTaxonomyClear}
        selectedCompanies={selectedCompanies}
        onCompanyToggle={onCompanyToggle}
        onCompanyClear={onCompanyClear}
      />
    </>
  )
}
